import React from 'react';
import {
  View, Text, StyleSheet,
  TouchableOpacity, Modal, TouchableWithoutFeedback,
} from 'react-native';
import { useConfigStore } from '@share/core';
import { useTheme } from '../hooks/useTheme';
import { Spacing, Radius, FontSize, FontWeight } from '../theme/tokens';
import { Icon } from '../atoms/Icon';
import { Toggle } from '../atoms/Toggle';

interface SettingsModalProps {
  visible: boolean;
  onClose: () => void;
}

export const SettingsModal: React.FC<SettingsModalProps> = ({ visible, onClose }) => {
  const { config, updateConfig }  = useConfigStore();
  const { colors, isDark }        = useTheme();

  const handleToggleNotifications = async (val: boolean) => {
    updateConfig({ notificationsEnabled: val });
    if (val && typeof window !== 'undefined' && 'Notification' in window) {
      if (Notification.permission === 'default') {
        try {
          await Notification.requestPermission();
        } catch {
          // ignore
        }
      }
    }
  };

  const handleToggleDarkMode = (enabled: boolean) => {
    updateConfig({ theme: enabled ? 'dark' : 'light' });
    if (typeof window !== 'undefined') {
      document.documentElement.setAttribute('data-theme', enabled ? 'dark' : 'light');
      localStorage.setItem('bitdrop_theme', enabled ? 'dark' : 'light');
    }
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <TouchableWithoutFeedback onPress={onClose}>
        <View style={styles.overlay}>
          <TouchableWithoutFeedback>
            <View style={[styles.modal, { backgroundColor: colors.surface, borderColor: colors.border }]}>

              {/* Header */}
              <View style={styles.header}>
                <Text style={[styles.title, { color: colors.text }]}>Settings</Text>
                <TouchableOpacity style={styles.closeBtn} onPress={onClose} activeOpacity={0.7}>
                  <Icon name="close" size={20} color={colors.text} />
                </TouchableOpacity>
              </View>

              {/* Dark Mode toggle */}
              <View style={[styles.settingRow, { backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderColor: colors.border }]}>
                <View>
                  <Text style={[styles.settingLabel, { color: colors.text }]}>Dark Mode</Text>
                  <Text style={[styles.settingSub, { color: colors.subtext }]}>{isDark ? 'Dark appearance active' : 'Light appearance active'}</Text>
                </View>
                <Toggle
                  value={isDark}
                  onValueChange={handleToggleDarkMode}
                  accessibilityLabel="Dark mode toggle"
                />
              </View>

              {/* Notifications toggle */}
              <View style={[styles.settingRow, { backgroundColor: isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)', borderColor: colors.border }]}>
                <View>
                  <Text style={[styles.settingLabel, { color: colors.text }]}>Push Notifications</Text>
                  <Text style={[styles.settingSub, { color: colors.subtext }]}>Notify when transfers finish</Text>
                </View>
                <Toggle
                  value={config.notificationsEnabled}
                  onValueChange={handleToggleNotifications}
                  accessibilityLabel="Push notifications toggle"
                />
              </View>

            </View>
          </TouchableWithoutFeedback>
        </View>
      </TouchableWithoutFeedback>
    </Modal>
  );
};

const styles = StyleSheet.create({
  overlay: {
    flex:            1,
    backgroundColor: 'rgba(0,0,0,0.5)',
    justifyContent:  'center',
    alignItems:      'center',
  },
  modal: {
    width:        '90%',
    maxWidth:     400,
    borderRadius: Radius.xl,
    borderWidth:  1,
    padding:      Spacing.xl,
    gap:          Spacing.md,
  } as any,
  header: {
    flexDirection:  'row',
    alignItems:     'center',
    justifyContent: 'space-between',
    marginBottom:   Spacing.xs,
  },
  title:    { fontSize: FontSize.lg, fontWeight: FontWeight.bold },
  closeBtn: { padding: Spacing.xs },
  settingRow: {
    flexDirection:  'row',
    justifyContent: 'space-between',
    alignItems:     'center',
    borderRadius:   Radius.lg,
    borderWidth:    1,
    padding:        Spacing.md,
  },
  settingLabel: { fontSize: FontSize.sm, fontWeight: FontWeight.bold },
  settingSub:   { fontSize: FontSize.xs, marginTop: 2 },
});
